import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import { ApiError, OpenAPI } from "./client"
import { ThemeProvider } from "./components/theme-provider"
import { Toaster } from "./components/ui/sonner"
import "./index.css"
import * as Sentry from "@sentry/react"
import { routeTree } from "./routeTree.gen"

// Cookie names (must match backend)
const ACCESS_TOKEN_COOKIE_NAME = "access_token"

OpenAPI.BASE = import.meta.env.VITE_API_URL
// TOKEN resolver is no longer needed - we're using HTTP-only cookies
// The backend will read the access_token from cookies
OpenAPI.TOKEN = async () => {
  // This is no longer used for Authorization header since we're using cookies
  // But we keep it for backward compatibility
  return ""
}
// Enable credentials (cookies) to be sent with requests
OpenAPI.WITH_CREDENTIALS = true

const handleApiError = (error: Error) => {
  if (error instanceof ApiError && [401, 403].includes(error.status)) {
    // Don't redirect if already on a public page (prevents infinite reload loop)
    const publicPaths = ["/login", "/recover-password", "/reset-password"]
    if (publicPaths.some((p) => window.location.pathname.startsWith(p))) {
      return
    }
    // Clear cookies on auth error
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks Firefox support
    document.cookie = `${ACCESS_TOKEN_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    window.location.href = "/login"
  }
}
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  mutationCache: new MutationCache({
    onError: handleApiError,
  }),
})

const router = createRouter({ routeTree })
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

Sentry.init({
  dsn: "https://2a6ec0c2f9b78447d0f57383fc564121@o4510757514969088.ingest.us.sentry.io/4510762453237760",
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  integrations: [
    // If you're using react router, use the integration for your react router version instead.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.browserTracingIntegration(),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  // Enable logs to be sent to Sentry
  enableLogs: true,
  // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
  environment: process.env.NODE_ENV,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [/^\//, import.meta.env.VITE_API_URL],
})

const container = document.getElementById("root")!
const root = ReactDOM.createRoot(container, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack)
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
})
root.render(
  <StrictMode>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster richColors closeButton />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)
